import type { ReRenderInputs, ReRenderSession, ReRenderSettings } from '../render/ReRenderSession.js';
import {
  frameTimestampMicros,
  type EncodedVideoChunkLike,
  type FrameSource,
  type VideoEncodeConfig,
  type VideoEncoderAdapter,
} from './VideoExport.js';

/**
 * Turns a re-render into a stream of encoded video chunks.
 *
 * The whole of Phase 7A, and deliberately thin: it owns no clock, no drawing, no
 * codec and no container. `ReRenderSession` already knows how to put a
 * performance back on screen one deterministic frame at a time; this drives that
 * loop and, after each frame, hands the picture to an encoder instead of asking
 * for a PNG.
 *
 * ## Why it drives the loop itself
 *
 * `ReRenderSession.renderAll` exists and would be the obvious thing to call. It
 * is not used here because encoding needs two things that belong between frames:
 * a capture taken *after* the draw, and a wait when the encoder is behind.
 * Reaching into `renderAll` to add those would put codec concerns inside the
 * renderer, which is the one arrangement §3 rules out. Driving `renderFrame` and
 * `advance` from outside keeps every phase's file doing one job.
 *
 * ## Memory
 *
 * Nothing is accumulated. A frame is drawn, captured, encoded, closed, and the
 * next one begins; chunks are handed to the caller as they arrive and are not
 * retained. The only bound that has to be enforced is the encoder's own queue,
 * because an encoder that is slower than the renderer will otherwise take frames
 * faster than it retires them and hold every one of them alive.
 */

/**
 * How many times an export may force the encoder to drain.
 *
 * Each one costs a key frame. A handful over a long export is invisible; a
 * hundred would mean something is wrong with the encoder rather than with the
 * bound, and at that point failing is more useful than quietly producing a file
 * that is all key frames.
 */
const MAX_FORCED_FLUSHES = 64;

export type VideoExportState =
  | 'idle'
  | 'configuring'
  | 'encoding'
  | 'flushing'
  | 'finished'
  | 'cancelled'
  | 'failed';

export interface VideoExportSettings extends ReRenderSettings {
  /**
   * How many frames the encoder may hold before the renderer waits.
   *
   * The backpressure valve. Left unbounded, a hardware encoder that falls behind
   * a fast renderer accumulates `VideoFrame`s — each one a GPU allocation — and
   * the export dies of exhaustion partway through rather than slowing down.
   *
   * The default is large because real encoders are: the hardware H.264 encoder
   * this was developed against runs with forty to sixty-five frames in flight
   * and does not emit its first chunk until it has been given about two dozen.
   * A small limit does not make it use less memory — it has already taken the
   * frames — it only forces the export to interrupt it, and every interruption
   * costs a key frame. Ninety-six sits above the working depth observed at every
   * resolution tried, so the limit is a genuine ceiling rather than a routine
   * obstacle.
   */
  readonly maxPendingFrames?: number;
  /**
   * How many frames may be outstanding while the encoder has emitted nothing.
   *
   * The startup window is the one place a larger allowance genuinely helps: the
   * encoder is filling a pipeline and has not had the chance to emit anything
   * yet, so interrupting it there is pure loss. Defaults to the ordinary limit.
   */
  readonly maxStartupFrames?: number;
  /** Frames between forced key frames. Zero leaves it to the encoder. */
  readonly keyFrameInterval?: number;
  /** Fingerprints these frames before encoding, for the determinism check. */
  readonly fingerprintFrames?: readonly number[];
}

export interface VideoExportProgress {
  readonly state: VideoExportState;
  readonly renderedFrames: number;
  readonly encodedFrames: number;
  readonly totalFrames: number;
  readonly droppedFrames: number;
  readonly encodeQueueSize: number;
  readonly liveFrames: number;
  readonly chunks: number;
  readonly bytes: number;
  readonly progress: number;
  readonly elapsedMs: number;
  readonly framesPerSecond: number;
  /** Times the encoder had to be drained explicitly. See `drainTo`. */
  readonly forcedFlushes: number;
  /** Real seconds of video produced per real second spent. 1 is realtime. */
  readonly realtimeRatio: number;
  readonly error: string | null;
}

export interface VideoExportHandlers {
  /**
   * Receives each chunk as it is produced.
   *
   * Awaited, so a slow sink slows the export rather than queueing behind it.
   * Phase 7B will put a muxer here; Phase 7A puts a counter.
   */
  readonly onChunk?: (chunk: EncodedVideoChunkLike) => void | Promise<void>;
  readonly onProgress?: (progress: VideoExportProgress) => void;
  /**
   * Called after each frame has been handed to the encoder.
   *
   * Awaited, and that is the whole point of it: Phase 7B has a second stream to
   * advance — audio — and it has to advance *with* the video rather than before
   * or after it. Doing it before means holding the entire encoded video in
   * memory while the audio catches up; doing it after means the same for the
   * audio. Interleaving keeps both bounded, and this is the seam that allows it
   * without the video loop knowing that audio exists.
   */
  readonly onFrameEncoded?: (index: number, timestampMicros: number) => void | Promise<void>;
  /** Frames per progress callback and per yield. */
  readonly yieldEvery?: number;
}

export interface VideoExportOptions {
  readonly session: ReRenderSession;
  readonly source: FrameSource;
  readonly encoder: VideoEncoderAdapter;
  readonly now?: () => number;
  readonly yieldToHost?: () => Promise<void>;
}

export class VideoExportSession {
  private readonly session: ReRenderSession;
  private readonly source: FrameSource;
  private readonly encoder: VideoEncoderAdapter;
  private readonly now: () => number;
  private readonly yieldToHost: () => Promise<void>;

  private state: VideoExportState = 'idle';
  private settings: VideoExportSettings | null = null;
  private cancelled = false;
  private error: string | null = null;

  private rendered = 0;
  private encoded = 0;
  private dropped = 0;
  private chunkCount = 0;
  private byteCount = 0;
  private startedAt = 0;
  private elapsed = 0;
  private readonly fingerprints: { frame: number; hash: string }[] = [];

  /**
   * Times the encoder was asked to drain because it was holding too much.
   *
   * Reported rather than hidden: each one costs a key frame and therefore some
   * compression, so a run with many of them is worth knowing about.
   */
  private forcedFlushes = 0;

  /** Split timings, for the benchmark §18 asks for. */
  private readonly timing = { render: 0, capture: 0, encode: 0, chunk: 0 };

  constructor(options: VideoExportOptions) {
    this.session = options.session;
    this.source = options.source;
    this.encoder = options.encoder;
    this.now = options.now ?? (() => Date.now());
    this.yieldToHost = options.yieldToHost ?? (() => Promise.resolve());
  }

  /**
   * Prepares the render and configures the encoder.
   *
   * Capture is forced off on the underlying session: it would produce the PNGs
   * this whole phase exists to stop making, and they would be built for every
   * frame and thrown away.
   */
  async prepare(
    inputs: ReRenderInputs,
    settings: VideoExportSettings,
    config: VideoEncodeConfig,
  ): Promise<VideoExportProgress> {
    this.reset();
    this.state = 'configuring';
    this.settings = settings;
    this.session.prepare(inputs, { ...settings, capture: false });

    try {
      await this.encoder.configure(config);
    } catch (cause) {
      this.error = cause instanceof Error ? cause.message : String(cause);
      this.state = 'failed';
    }
    return this.progress();
  }

  /**
   * Renders and encodes every frame.
   *
   * The order inside the loop is the phase's whole subject: draw, capture the
   * thing that was drawn, wait if the encoder is behind, encode, release. The
   * release is unconditional — a frame that failed to encode still has to be
   * closed or it leaks exactly as one that succeeded would.
   */
  async run(handlers: VideoExportHandlers = {}): Promise<VideoExportProgress> {
    if (this.state === 'failed') return this.progress();
    const settings = this.settings;
    if (settings === null) return this.progress();

    this.state = 'encoding';
    this.startedAt = this.now();
    const fps = settings.fps;
    const maxPending = Math.max(1, settings.maxPendingFrames ?? 96);
    const maxStartup = Math.max(maxPending, settings.maxStartupFrames ?? maxPending);
    const keyEvery = Math.max(0, settings.keyFrameInterval ?? Math.round(fps * 2));
    const yieldEvery = Math.max(1, handlers.yieldEvery ?? 4);
    const wanted = new Set(settings.fingerprintFrames ?? []);

    try {
      for (;;) {
        if (this.cancelled) break;

        // 1. Draw. Everything about determinism is already settled inside here.
        const renderStart = this.now();
        await this.session.renderFrame();
        const index = this.session.progress().frame;
        this.timing.render += this.now() - renderStart;
        this.rendered += 1;

        if (wanted.has(index)) {
          this.fingerprints.push({ frame: index, hash: await this.session.fingerprintFrame() });
        }

        // 2. Capture what was drawn, at the timestamp this frame index implies.
        const captureStart = this.now();
        const timestamp = frameTimestampMicros(index, fps);
        const frame = await this.source.capture(index, timestamp);
        this.timing.capture += this.now() - captureStart;

        if (frame === null) {
          this.dropped += 1;
        } else {
          // 3. Wait for the encoder if it is behind. This is the only place the
          // export is allowed to grow memory, and this is what stops it.
          await this.drainTo(maxPending, maxStartup);
          if (this.cancelled) {
            frame.close();
            break;
          }

          // 4. Encode, and release whatever happens.
          const encodeStart = this.now();
          try {
            this.encoder.encode(frame, { keyFrame: keyEvery > 0 && index % keyEvery === 0 });
            this.encoded += 1;
          } catch (cause) {
            this.error = cause instanceof Error ? cause.message : String(cause);
            frame.close();
            this.state = 'failed';
            break;
          } finally {
            this.timing.encode += this.now() - encodeStart;
          }
          frame.close();
        }

        if (handlers.onFrameEncoded !== undefined) {
          await handlers.onFrameEncoded(index, timestamp);
        }

        if (index % yieldEvery === 0) {
          this.elapsed = this.now() - this.startedAt;
          handlers.onProgress?.(this.progress());
          await this.yieldToHost();
        }

        if (!this.session.advance()) break;
      }

      if (this.state !== 'failed') {
        this.state = this.cancelled ? 'cancelled' : 'flushing';
        // Even a cancelled export flushes: the encoder holds frames, and closing
        // it without draining leaves them alive until it is collected.
        await this.encoder.flush();
        if (!this.cancelled) this.state = 'finished';
      }
    } catch (cause) {
      this.error = cause instanceof Error ? cause.message : String(cause);
      this.state = 'failed';
    }

    this.elapsed = this.now() - this.startedAt;
    handlers.onProgress?.(this.progress());
    return this.progress();
  }

  /**
   * Waits until the encoder has fewer than `limit` frames outstanding.
   *
   * Yields rather than spinning: the encoder retires frames on the host's own
   * tasks, so a busy loop here would starve exactly the work it is waiting for.
   * Bounded so a wedged encoder fails the export instead of hanging it.
   *
   * ## The startup window
   *
   * There are two limits because there are two situations that look identical
   * from here — a full queue and no output — and only one of them is a reason
   * to wait.
   *
   * A hardware encoder does not emit a chunk per frame. It fills a pipeline
   * first: on the machine this was written on, twenty-four frames go in before
   * the first one comes out, at every resolution tried. Applying the
   * steady-state limit during that window deadlocks the export outright — the
   * queue cannot fall until the encoder emits, and the encoder cannot emit
   * until it is given more frames than the limit permits, so the loop waits for
   * something it is itself preventing. That was the shape of the bug this
   * replaced: the export failed with "encoder did not drain below 8 frames"
   * having produced no chunks at all, which reads like a broken encoder and is
   * not one.
   *
   * So while the encoder has emitted nothing, the wider bound applies — memory
   * is still bounded, just at a size that lets the pipeline fill.
   *
   * ## And afterwards
   *
   * Dropping straight back to the configured limit once the first chunk arrives
   * is not enough either. By then the queue holds a pipeline's worth of frames,
   * and an encoder in steady state retires roughly one per frame it is given —
   * so a limit below its pipeline depth means waiting, every frame, for a
   * backlog it has no reason to clear. The export does not deadlock, it just
   * crawls.
   *
   * ## Reaching the limit
   *
   * Once the encoder is emitting, the configured limit is a hard ceiling and
   * stays one — a caller that asks for at most N frames outstanding gets that.
   * What changes is the response to reaching it. Waiting is wrong, because the
   * encoder has no obligation to retire anything on its own; but there is a way
   * to ask, and it is `flush`. So the queue is drained explicitly and the loop
   * continues.
   *
   * A flush costs a key frame and therefore some compression, which is why the
   * default limit is set above the depth real encoders were observed working
   * at rather than at a tidy small number. An export that trips the limit every
   * sixty frames comes out noticeably larger than one that does not. Only an
   * encoder still over the limit *after* a flush is genuinely wedged, and that
   * one fails the export.
   */
  private async drainTo(limit: number, startupLimit = limit): Promise<void> {
    let waits = 0;
    let flushedThisWait = false;
    for (;;) {
      if (this.cancelled) return;
      const produced = this.encoder.producedChunks;
      const queue = this.encoder.queueSize;
      const bound = produced === 0 ? startupLimit : limit;
      if (queue < bound) return;

      // Over the bound. Ask it to drain before concluding anything: an encoder
      // holding frames because it is buffering will empty on request, and one
      // that will not is the only kind worth failing over.
      if (!flushedThisWait && this.forcedFlushes < MAX_FORCED_FLUSHES) {
        flushedThisWait = true;
        this.forcedFlushes += 1;
        await this.encoder.flush();
        continue;
      }

      await this.yieldToHost();
      waits += 1;
      if (waits > 10_000) {
        throw new Error(
          `encoder did not drain below ${bound} frames after a flush ` +
          `(queue ${queue}, chunks produced ${this.encoder.producedChunks ?? 'unknown'})`,
        );
      }
    }
  }

  /** Receives a chunk from the host's encoder callback. */
  acceptChunk(chunk: EncodedVideoChunkLike, sink?: VideoExportHandlers['onChunk']): void | Promise<void> {
    this.chunkCount += 1;
    this.byteCount += chunk.byteLength;
    const started = this.now();
    const result = sink?.(chunk);
    this.timing.chunk += this.now() - started;
    return result;
  }

  /**
   * Stops the export.
   *
   * Everything after this is release: the loop exits at its next check, the
   * frame in hand is closed, the encoder is flushed and closed, and the caller
   * puts the live session back. Nothing here decides *what* the live session
   * looks like — that belongs to whoever set it aside.
   */
  cancel(): void {
    this.cancelled = true;
    this.session.cancel();
  }

  /** Releases the encoder and the capture path. Safe to call twice. */
  dispose(): void {
    try {
      this.encoder.close();
    } catch {
      // An encoder that is already closed is not a failure worth propagating
      // out of teardown.
    }
    this.source.dispose();
  }

  private reset(): void {
    this.state = 'idle';
    this.cancelled = false;
    this.error = null;
    this.rendered = 0;
    this.forcedFlushes = 0;
    this.encoded = 0;
    this.dropped = 0;
    this.chunkCount = 0;
    this.byteCount = 0;
    this.elapsed = 0;
    this.startedAt = 0;
    this.fingerprints.length = 0;
    this.timing.render = 0;
    this.timing.capture = 0;
    this.timing.encode = 0;
    this.timing.chunk = 0;
  }

  progress(): VideoExportProgress {
    const total = this.session.progress().totalFrames;
    const seconds = this.elapsed / 1000;
    const settings = this.settings;
    const videoSeconds = settings === null ? 0 : this.rendered / Math.max(1, settings.fps);
    return {
      state: this.state,
      renderedFrames: this.rendered,
      encodedFrames: this.encoded,
      totalFrames: total,
      droppedFrames: this.dropped,
      encodeQueueSize: this.encoder.queueSize,
      liveFrames: this.source.liveFrames,
      chunks: this.chunkCount,
      bytes: this.byteCount,
      progress: total === 0 ? 0 : Math.min(1, this.rendered / total),
      elapsedMs: Math.round(this.elapsed),
      framesPerSecond: seconds > 0 ? Number((this.rendered / seconds).toFixed(2)) : 0,
      forcedFlushes: this.forcedFlushes,
      realtimeRatio: seconds > 0 ? Number((videoSeconds / seconds).toFixed(3)) : 0,
      error: this.error,
    };
  }

  /** The split timings, in milliseconds. See §18. */
  timings(): {
    readonly renderMs: number;
    readonly captureMs: number;
    readonly encodeMs: number;
    readonly chunkMs: number;
    readonly totalMs: number;
    readonly perFrame: {
      readonly renderMs: number;
      readonly captureMs: number;
      readonly encodeMs: number;
    };
  } {
    const frames = Math.max(1, this.rendered);
    return {
      renderMs: Math.round(this.timing.render),
      captureMs: Math.round(this.timing.capture),
      encodeMs: Math.round(this.timing.encode),
      chunkMs: Math.round(this.timing.chunk),
      totalMs: Math.round(this.elapsed),
      perFrame: {
        renderMs: Number((this.timing.render / frames).toFixed(3)),
        captureMs: Number((this.timing.capture / frames).toFixed(3)),
        encodeMs: Number((this.timing.encode / frames).toFixed(3)),
      },
    };
  }

  /** Frame fingerprints taken before encoding. See §15. */
  fingerprint(): {
    readonly frames: readonly { readonly frame: number; readonly hash: string }[];
    readonly combined: string;
  } {
    return {
      frames: [...this.fingerprints],
      combined: this.fingerprints.map((entry) => `${entry.frame}:${entry.hash}`).join('|'),
    };
  }
}
